import type { ReactElement, ReactNode, MouseEvent } from 'react'
import styled, { css } from 'styled-components'
import { Button } from 'suomifi-ui-components'

/**
 * Tuhoava painike kahdessa visuaalisessa muodossa:
 * - `tertiary` (oletus): tumma punainen teksti + ikoni, ei borderia, ei
 *   taustaa. Käytetään sivupalkin "Poista käyttäjä" -painikkeena, jotta
 *   tuhoava toiminto on visuaalisesti erotettu lukitus-tason painikkeista.
 * - `primary`: täyteväritetty punainen — käytetään modaalin
 *   vahvistuspainikkeena ("Poista käyttäjä"). Korkein visuaalinen paino,
 *   koska se on poistoa vahvistava päätoiminto.
 *
 * Toteutettu wrapping div:llä DS-Buttonin ympärille, koska styled(Button)
 * tuottaa DS:n overloadattujen prop-tyyppien takia "union too complex"
 * -tyyppivirheen.
 *
 * DS:n design-tokeneissa on vain `alertBase` ja `alertLight1` — `alertDark1`
 * puuttuu. Hover-tila käyttää manuaalista tummennusta `hsl(3, 59%, 33%)`
 * (alertBase 43% → 33% lightness).
 */

type Variant = 'tertiary' | 'primary'

interface Props {
  tuhoavaVariantti?: Variant
  children: ReactNode
  icon?: ReactElement
  onClick?: (event: MouseEvent<Element>) => void
  disabled?: boolean
}

const ALERT_DARK = 'hsl(3, 59%, 33%)'

const tertiaryStyles = css`
  & .fi-button {
    background: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.colors.alertBase};
    box-shadow: none;
  }
  & .fi-button:hover,
  & .fi-button:focus {
    background: ${({ theme }) => theme.colors.alertLight1};
    border-color: transparent;
    color: ${ALERT_DARK};
  }
  & .fi-button .fi-icon path {
    fill: ${({ theme }) => theme.colors.alertBase};
  }
  & .fi-button:hover .fi-icon path,
  & .fi-button:focus .fi-icon path {
    fill: ${ALERT_DARK};
  }
`

const primaryStyles = css`
  & .fi-button {
    background: ${({ theme }) => theme.colors.alertBase};
    border-color: ${({ theme }) => theme.colors.alertBase};
    color: ${({ theme }) => theme.colors.whiteBase};
  }
  & .fi-button:hover,
  & .fi-button:focus {
    background: ${ALERT_DARK};
    border-color: ${ALERT_DARK};
    color: ${({ theme }) => theme.colors.whiteBase};
  }
  & .fi-button .fi-icon path {
    fill: ${({ theme }) => theme.colors.whiteBase};
  }
`

const Wrap = styled.span<{ $variant: Variant }>`
  /* tertiary (sivupalkki): display:contents → kääre läpinäkyvä layoutille, jotta
     nappi on suora lapsi ja esim. ToiminnotWrapin width:100% pätee.
     primary (modaalin footer): inline-flex → kääre on aito flex-lapsi, jolloin
     ModalFooterin "& > *" -marginaalit ja kohdistus pätevät ja nappi on
     samankokoinen kuin muut footerin napit (esim. Peruuta). display:contents
     ohitti tämän → nappi oli väärän kokoinen. */
  display: ${({ $variant }) =>
    $variant === 'primary' ? 'inline-flex' : 'contents'};
  ${({ $variant }) => ($variant === 'primary' ? primaryStyles : tertiaryStyles)}
`

export default function TuhoavaButton({
  tuhoavaVariantti = 'tertiary',
  children,
  icon,
  onClick,
  disabled,
}: Props) {
  const dsVariant =
    tuhoavaVariantti === 'primary' ? 'default' : 'secondaryNoBorder'
  return (
    <Wrap $variant={tuhoavaVariantti}>
      <Button variant={dsVariant} icon={icon} onClick={onClick} disabled={disabled}>
        {children}
      </Button>
    </Wrap>
  )
}
